import {logger} from '@vio/core/src/utils/logging/logger'
import {capitalizeFirstLetter} from '@vio/core/src/utils/string'

import {getSortedFilters, SortedFilter} from './getSeoSRPPath'
import {translations} from './translations'
import {extractMetadataFromNavPathInfo} from './utils'

import type {NavPathInfo} from '@findhotel/sapi'
import type {IntlShape} from 'react-intl'
import type {LinkRobots} from 'seo/getLinksMetadata'
import type {SeoAttributes} from 'seo/seo-db/attributes'

const log = logger.child({
  module: 'getSeoMetadata'
})

const TITLE_SUFFIX = ' | Vio.com'

const MAX_ATTRIBUTES_IN_SEO = 3
const MINIMUM_RESULTS_FOR_INDEXING = 10
const ALLOW_INDEXING =
  process.env.NEXT_PUBLIC_ENV === 'prd' ||
  process.env.NODE_ENV === 'development' ||
  process.env.NODE_ENV === 'test'

type FormatSeoMessageType = (translationId: string) => string

export interface SeoMetadata {
  heading: string
  title: string
  description: string
}

export interface IndexMetadata {
  robots: 'index, follow' | 'noindex, follow' | 'noindex, nofollow'
  canonical: string | null
}

interface GetSeoMetadataFromNavPathInfoParams {
  placeId: string
  filterSlugs: string[]
  locale: string
  seoAttributes: SeoAttributes
  navPathInfo?: NavPathInfo
  placeName?: string
  formatMessage: IntlShape['formatMessage']
}

/**
 * Gets SEO metadata from navPathInfo and placeName.
 * This allows reusing the function with different data sources (e.g., seo-db places).
 */
export const getSeoMetadataFromNavPathInfo = ({
  placeId,
  filterSlugs,
  locale,
  seoAttributes,
  navPathInfo,
  placeName,
  formatMessage
}: GetSeoMetadataFromNavPathInfoParams): SeoMetadata | null => {
  if (!navPathInfo || !locale) {
    return null
  }

  const {country, city, landmark} = extractMetadataFromNavPathInfo(
    placeId,
    navPathInfo,
    placeName
  )

  if (!country && !city && !landmark) {
    log.error(`No metadata found`, {locale, placeId, navPathInfo})
    return null
  }

  return buildSeoMetadata({
    country: country?.name,
    city: city?.name,
    landmark: landmark?.name,
    filterSlugs,
    seoAttributes,
    formatMessage
  })
}

/**
 * Get indexing-related meta tags for SEO SRP.
 * seo-api's `robots` is used as is when it answered. Without an answer (an
 * outage, or a response cached before seo-api returned `robots`) the SRP falls
 * back to its locale and result count, so an outage does not deindex every SRP.
 */
export const getIndexingMetadata = ({
  baseUrl,
  canonicalPath,
  initialResultsCount,
  isSeoActiveLocale,
  seoApiRobots
}: {
  baseUrl: string
  canonicalPath: string | null
  initialResultsCount: number
  isSeoActiveLocale: boolean
  seoApiRobots?: LinkRobots
}): IndexMetadata => {
  const fallbackRobots =
    isSeoActiveLocale && initialResultsCount >= MINIMUM_RESULTS_FOR_INDEXING
      ? 'index, follow'
      : 'noindex, nofollow'

  const selectedRobots = seoApiRobots ?? fallbackRobots
  const robots = ALLOW_INDEXING ? selectedRobots : 'noindex, nofollow'
  const isIndexable = !robots.includes('noindex')

  return {
    robots,
    canonical:
      isIndexable && canonicalPath ? `${baseUrl}${canonicalPath}` : null
  }
}

const retrieveCitySeoMetadata = (
  amountOfFilters: number,
  formatSeoMessage: FormatSeoMessageType
) => {
  if (!amountOfFilters) {
    const heading = formatSeoMessage('seoMeta.cityHeading')

    return {
      heading,
      title: `${heading}${TITLE_SUFFIX}`,
      description: formatSeoMessage('seoMeta.cityDescription')
    }
  }

  const heading = formatSeoMessage('seoMeta.cityAttrHeading')
  const description = formatSeoMessage('seoMeta.cityAttrDescription')

  return {
    heading,
    title: `${heading}${TITLE_SUFFIX}`,
    description: `${heading}. ${description}`
  }
}

const retrieveCountrySeoMetadata = (
  amountOfFilters: number,
  formatSeoMessage: FormatSeoMessageType
) => {
  if (!amountOfFilters) {
    const heading = formatSeoMessage('seoMeta.countryHeading')

    return {
      heading,
      title: `${heading}${TITLE_SUFFIX}`,
      description: formatSeoMessage('seoMeta.countryDescription')
    }
  }

  const heading = formatSeoMessage('seoMeta.countryAttrHeading')
  const description = formatSeoMessage('seoMeta.countryAttrDescription')

  return {
    heading,
    title: `${heading}${TITLE_SUFFIX}`,
    description: `${heading}. ${description}`
  }
}

const retrieveLandmarkSeoMetadata = (
  amountOfFilters: number,
  formatSeoMessage: FormatSeoMessageType
) => {
  if (!amountOfFilters) {
    const heading = formatSeoMessage('seoMeta.landmarkHeading')

    return {
      heading,
      title: `${heading}${TITLE_SUFFIX}`,
      description: formatSeoMessage('seoMeta.landmarkDescription')
    }
  }

  const heading = formatSeoMessage('seoMeta.landmarkAttrHeading')
  const description = formatSeoMessage('seoMeta.landmarkAttrDescription')

  return {
    heading,
    title: `${heading}${TITLE_SUFFIX}`,
    description: `${heading}. ${description}`
  }
}

interface BuildSeoMetadataParams {
  country: string
  city: string
  landmark: string
  filterSlugs: string[]
  seoAttributes: SeoAttributes
  formatMessage: IntlShape['formatMessage']
}

const PIPE_SEPARATOR = ' | '

export const buildSeoMetadata = ({
  country,
  city,
  landmark,
  filterSlugs,
  seoAttributes,
  formatMessage
}: BuildSeoMetadataParams) => {
  const sortedFilters = getSortedFilters(filterSlugs, seoAttributes)
  const hotels = formatHotelWithAttributes(
    sortedFilters.slice(0, MAX_ATTRIBUTES_IN_SEO)
  )
  const values = {
    hotels,
    country,
    city,
    landmark
  }

  const formatSeoMessage = (translationId: string) => {
    const message = formatMessage(translations[translationId], values)
    return message ? capitalizeFirstLetter(message) : message
  }

  if (landmark) {
    return retrieveLandmarkSeoMetadata(sortedFilters.length, formatSeoMessage)
  }

  if (city) {
    return retrieveCitySeoMetadata(sortedFilters.length, formatSeoMessage)
  }

  return retrieveCountrySeoMetadata(sortedFilters.length, formatSeoMessage)
}

const formatHotelWithAttributes = (filters: SortedFilter[]): string => {
  if (filters.length === 0) return ''

  const [first, ...remaining] = filters
  return [first.hotelDescription, ...remaining.map(f => f.label)].join(
    PIPE_SEPARATOR
  )
}
